import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useData } from '../context/DataContext';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';
import { lineTotal } from '../utils/lineMath';
import { isInstallJob } from '../utils/boardColumns';

// ── Shared styles (light theme, consistent with the rest of the app) ──
const darkButtonStyle = {
  background: '#FFFFFF',
  border: '1px solid #E3ECF5',
  color: '#03045E',
  padding: '10px 18px',
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background 0.3s',
};
const labelStyle = {
  color: '#6B7C93',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1,
  textTransform: 'uppercase',
  marginBottom: 6,
  display: 'block',
};
const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: '#FFFFFF',
  border: '1px solid #E3ECF5',
  borderRadius: 10,
  color: '#03045E',
  fontSize: 14,
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
const cardStyle = {
  background: '#FFFFFF',
  border: '1px solid #E3ECF5',
  borderRadius: 16,
};

// Money with thousands separators + 2 decimals, e.g. $1,200.00
const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Maps a product category to a high-level material group for the comparison chart.
const getMaterialGroup = (category) => {
  if (!category) return null;
  // Tolerant match: trim + lowercase so minor formatting differences in the
  // stored product.category don't drop data.
  const norm = String(category).trim().toLowerCase();
  const chainLinkCats = [
    'Chain Link', 'Galv Pipe', 'Black Pipe', 'Galv Fittings', 'Black Fittings',
    'Galv Gates Residential', 'Galv Gates Commercial',
    'Black Gates Residential', 'Black Gates Commercial', 'Cantilever Gate',
    // Also match the category names this app actually uses for gates
    'Residential Swing Gates Galv', 'Commercial Swing Gates Galv',
    'Residential Gates Black', 'Commercial Gates Black', 'Aluminum Slide Gate Hardware',
  ].map(s => s.toLowerCase());
  const woodCats = ['Wood', 'Wood Hardware', 'Split Rail'].map(s => s.toLowerCase());
  if (chainLinkCats.includes(norm)) return 'Chain Link';
  if (woodCats.includes(norm)) return 'Wood';
  if (norm === 'pvc') return 'PVC';
  if (norm === 'aluminum') return 'Aluminum';
  return null;
};

// `muted` renders the value as NOT APPLICABLE rather than as a figure: same
// size, position and line height — so the card's height never changes and the
// row cannot shift — but tertiary grey and a lighter weight, so a dash cannot
// be mistaken for a live number or for a value that failed to load.
function StatCard({ label, value, accent, muted = false }) {
  const accents = {
    cyan: '#0077B6',
    violet: '#00B4D8',
    green: '#0F6E56',
    orange: '#48CAE4',
  };
  const accentColor = accents[accent] || accents.cyan;
  return (
    <div style={{
      ...cardStyle,
      padding: 24,
      borderTop: `3px solid ${accentColor}`,
    }}>
      <p style={{ color: '#6B7C93', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, margin: 0 }}>
        {label}
      </p>
      <p style={{
        fontSize: 36, fontWeight: muted ? 500 : 800,
        color: muted ? '#9AA8BC' : '#03045E',
        margin: '10px 0 0 0', lineHeight: 1,
      }}>
        {value}
      </p>
    </div>
  );
}

function StockProgressRow({ product, type }) {
  const stock = parseFloat(product.stockQty) || 0;
  const reorder = parseFloat(product.reorderPoint) || 1;
  const percent = type === 'out' ? 0 : Math.max(0, Math.min(100, (stock / reorder) * 100));
  const barColor = type === 'out' ? '#A32D2D' : '#854F0B';
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid #E3ECF5' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ color: '#03045E', fontSize: 13, fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {product.description}
          </p>
          <p style={{ color: '#6B7C93', fontSize: 10, margin: '2px 0 0 0' }}>
            <span style={{ color: '#0077B6', fontFamily: 'monospace', fontWeight: 700 }}>{product.sku}</span>
          </p>
        </div>
        <p style={{ color: barColor, fontSize: 13, fontWeight: 700, marginLeft: 12, whiteSpace: 'nowrap' }}>
          {stock} / {reorder}
        </p>
      </div>
      <div style={{ height: 6, background: '#E3ECF5', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${percent}%`, background: barColor, borderRadius: 3, transition: 'width 0.5s' }} />
      </div>
    </div>
  );
}

// ── COUNTER SALES vs INSTALLATION JOBS ────────────────────────────────────
//
// Installation is a different business from the counter, and its jobs used to
// be counted inside counter revenue. This mode separates them.
//
// IT APPLIES TO THE MONEY FIGURES ONLY:
//   Revenue This Month, Revenue Year to Date, Quotes This Year,
//   Invoices This Year, and the dollars behind Sales by Material.
//
// IT DOES NOT APPLY TO INVENTORY. Material leaves the warehouse whether a crew
// installs it or a customer carries it out, so Out of Stock and Low Stock cover
// every job in every mode — they are computed from the products collection and
// never touch a document at all. The inventory section says so on screen.
//
// An install job is identified by its CURRENT board status, via isInstallJob in
// utils/boardColumns.js — the stored key lives there, not here.
const SALES_MODES = [
  { key: 'counter', label: 'Counter sales', note: 'Installation jobs excluded' },
  { key: 'install', label: 'Installation jobs', note: 'Installation jobs only' },
  { key: 'all', label: 'All', note: 'Counter sales and installation jobs' },
];

// The documents a money figure counts, for the selected mode. Anything with no
// board status — every quote, and older invoices — counts as a counter sale:
// it was never marked as installation.
const applySalesMode = (docs, mode) => {
  if (mode === 'all') return docs;
  if (mode === 'install') return docs.filter(d => isInstallJob(d));
  return docs.filter(d => !isInstallJob(d));
};

function Reports({ user }) {
  const { products } = useData();

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);

  // Filters
  const [dateRangeStart, setDateRangeStart] = useState(null);
  const [dateRangeEnd, setDateRangeEnd] = useState(null);
  const [salespersonFilter, setSalespersonFilter] = useState('All');
  // Counter sales vs installation jobs. MONEY FIGURES ONLY — see SALES_MODES
  // and the inventory note below. Defaults to counter sales: installation is a
  // different business and was never meant to be inside counter revenue.
  const [salesMode, setSalesMode] = useState('counter');

  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    setLoading(true);
    try {
      // Fetch only documents the report can actually display, instead of scanning
      // whole collections. Every displayed metric is internally capped at
      // year-to-date (revenue/quotes/invoices YTD, revenue this month) or the
      // trailing-30-day material chart. The widest boundary is therefore the
      // EARLIER of start-of-year and 30-days-ago — the 30-day case only wins in
      // early January, when it reaches back into the previous December. createdAt
      // is stored as an ISO 8601 string (new Date().toISOString() in buildDocData),
      // and ISO strings sort chronologically, so a string bound compares correctly.
      const now = new Date();
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      // Boundaries above are built in LOCAL time; toISOString() converts to UTC,
      // shifting the bound forward by the timezone offset. Subtract a 2-day safety
      // pad so no boundary-day document is excluded by the fetch (2 days covers any
      // timezone offset + DST). This only widens the FETCH; every client-side
      // aggregation below keeps its exact original boundary, so no displayed number
      // changes.
      const rangeStartDate = new Date(Math.min(startOfYear.getTime(), thirtyDaysAgo.getTime()));
      rangeStartDate.setDate(rangeStartDate.getDate() - 2);
      const rangeStart = rangeStartDate.toISOString();

      // Some (legacy) documents store their timestamp only in `date`, not
      // `createdAt`. A single where('createdAt', …) filter silently drops those,
      // which emptied the 30-day material chart. Run TWO bounded range queries per
      // collection — one on createdAt, one on date — and union the results by doc
      // id. This reproduces the client code's `createdAt || date` semantics while
      // still fetching only an in-range slice. Both `date` and `createdAt` are
      // stored as ISO strings, so the same rangeStart bound compares correctly.
      const [invByCreated, invByDate, quoteByCreated, quoteByDate] = await Promise.all([
        getDocs(query(collection(db, 'invoices'), where('createdAt', '>=', rangeStart))),
        getDocs(query(collection(db, 'invoices'), where('date', '>=', rangeStart))),
        getDocs(query(collection(db, 'quotes'), where('createdAt', '>=', rangeStart))),
        getDocs(query(collection(db, 'quotes'), where('date', '>=', rangeStart))),
      ]);
      // Merge result sets, deduping by Firestore doc id (a doc with both fields
      // appears in both snapshots — keep a single copy).
      const mergeById = (...snaps) => {
        const map = new Map();
        snaps.forEach(snap => snap.docs.forEach(d => {
          if (!map.has(d.id)) map.set(d.id, { id: d.id, ...d.data() });
        }));
        return Array.from(map.values());
      };
      setInvoices(mergeById(invByCreated, invByDate));
      setQuotes(mergeById(quoteByCreated, quoteByDate));
    } catch (err) {
      console.error('Error loading report data:', err);
    }
    setLoading(false);
  };

  // ── Filtered datasets ──
  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      if (salespersonFilter !== 'All' && inv.salesperson !== salespersonFilter) return false;
      if (dateRangeStart || dateRangeEnd) {
        const d = new Date(inv.createdAt || inv.date);
        if (dateRangeStart && d < new Date(dateRangeStart)) return false;
        if (dateRangeEnd && d > new Date(dateRangeEnd + 'T23:59:59')) return false;
      }
      return true;
    });
  }, [invoices, salespersonFilter, dateRangeStart, dateRangeEnd]);

  const filteredQuotes = useMemo(() => {
    return quotes.filter(q => {
      if (salespersonFilter !== 'All' && q.salesperson !== salespersonFilter) return false;
      if (dateRangeStart || dateRangeEnd) {
        const d = new Date(q.createdAt || q.date);
        if (dateRangeStart && d < new Date(dateRangeStart)) return false;
        if (dateRangeEnd && d > new Date(dateRangeEnd + 'T23:59:59')) return false;
      }
      return true;
    });
  }, [quotes, salespersonFilter, dateRangeStart, dateRangeEnd]);

  // ── The MONEY datasets ──
  // Every dollar figure and every document COUNT below is built from these, so
  // the mode cannot be applied to one figure and forgotten on another.
  // filteredInvoices / filteredQuotes stay as they are for everything else (the
  // salesperson list, and anything that must see all jobs).
  const moneyInvoices = useMemo(
    () => applySalesMode(filteredInvoices, salesMode), [filteredInvoices, salesMode]);
  const moneyQuotes = useMemo(
    () => applySalesMode(filteredQuotes, salesMode), [filteredQuotes, salesMode]);

  // Sales by material — last 30 days of (filtered) invoices only.
  //
  // MONEY, and it follows the mode: these tiles are revenue in dollars split by
  // material, not a count of stock movements. In counter-sales mode they show
  // the material mix of COUNTER revenue. (The stock figures further down are
  // the ones that always cover every job.)
  // Category is resolved per line item via productId (falling back to SKU, since a
  // product re-import can reassign Firestore doc ids while preserving the SKU that
  // line items store). Return credits contribute negatively via lineTotal. The
  // denominator is ALL in-window line items (including non-4 / no-product "Other"),
  // so each tile's percentage is a true share of total sales.
  const salesByMaterial = useMemo(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const totals = { 'Chain Link': 0, 'Wood': 0, 'PVC': 0, 'Aluminum': 0 };
    let grandTotal = 0;

    moneyInvoices.forEach(inv => {
      const invDate = new Date(inv.createdAt || inv.date);
      if (invDate < thirtyDaysAgo) return;
      (inv.lineItems || []).forEach(item => {
        const amount = lineTotal(item);   // signed: return credits are negative
        grandTotal += amount;             // denominator includes every line item
        if (!item.productId) return;      // non-stock custom → Other (denominator only)
        const product = products.find(p => p.id === item.productId)
          || (item.sku ? products.find(p => p.sku === item.sku) : null);
        if (!product) return;             // orphaned productId → Other
        const group = getMaterialGroup(product.category);
        if (group) totals[group] += amount;
      });
    });

    const TILES = [
      { key: 'Chain Link', emoji: '🔗', color: '#0077B6' },
      { key: 'Wood', emoji: '🪵', color: '#00B4D8' },
      { key: 'PVC', emoji: '⬜', color: '#48CAE4' },
      { key: 'Aluminum', emoji: '✨', color: '#90A4B8' },
    ];
    return TILES.map(t => ({
      ...t,
      amount: totals[t.key],
      pct: grandTotal > 0 ? (totals[t.key] / grandTotal) * 100 : 0,
    }));
  }, [moneyInvoices, products]);

  const revenueThisMonth = useMemo(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return moneyInvoices
      .filter(inv => new Date(inv.createdAt || inv.date) >= startOfMonth)
      .reduce((sum, inv) => sum + (parseFloat(inv.grandTotal) || 0), 0);
  }, [moneyInvoices]);

  const revenueYTD = useMemo(() => {
    const startOfYear = new Date(new Date().getFullYear(), 0, 1);
    return moneyInvoices
      .filter(inv => new Date(inv.createdAt || inv.date) >= startOfYear)
      .reduce((sum, inv) => sum + (parseFloat(inv.grandTotal) || 0), 0);
  }, [moneyInvoices]);

  const quotesYTD = useMemo(() => {
    const startOfYear = new Date(new Date().getFullYear(), 0, 1);
    return moneyQuotes.filter(q => new Date(q.createdAt || q.date) >= startOfYear).length;
  }, [moneyQuotes]);

  const invoicesYTD = useMemo(() => {
    const startOfYear = new Date(new Date().getFullYear(), 0, 1);
    return moneyInvoices.filter(inv => new Date(inv.createdAt || inv.date) >= startOfYear).length;
  }, [moneyInvoices]);

  const stockAlerts = useMemo(() => {
    const outOfStock = [];
    const lowStock = [];
    products.forEach(p => {
      if (!p.trackInventory) return;
      const stock = parseFloat(p.stockQty) || 0;
      const reorder = parseFloat(p.reorderPoint) || 0;
      if (stock <= 0) {
        outOfStock.push({ ...p, stockQty: stock, reorderPoint: reorder });
      } else if (stock <= reorder) {
        lowStock.push({ ...p, stockQty: stock, reorderPoint: reorder });
      }
    });
    outOfStock.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
    lowStock.sort((a, b) => (a.stockQty / (a.reorderPoint || 1)) - (b.stockQty / (b.reorderPoint || 1)));
    return { outOfStock, lowStock };
  }, [products]);

  const activeMode = SALES_MODES.find(m => m.key === salesMode) || SALES_MODES[0];

  const salespersons = useMemo(() => {
    const set = new Set();
    invoices.forEach(inv => { if (inv.salesperson) set.add(inv.salesperson); });
    quotes.forEach(q => { if (q.salesperson) set.add(q.salesperson); });
    return Array.from(set).sort();
  }, [invoices, quotes]);

  return (
    <>
      <Sidebar />
      <div style={{ minHeight: '100vh', marginLeft: SIDEBAR_W, background: '#CDE7F5', padding: 24 }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ ...cardStyle, padding: '20px 24px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div>
              {/* The mode is IN THE HEADING, so a printed or screenshotted
                  report states what its money figures cover. */}
              <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#03045E' }}>
                Reports — {activeMode.label}
              </h1>
              <p style={{ color: '#6B7C93', fontSize: 12, margin: 0, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 600 }}>
                Sales figures: {activeMode.note} · Inventory: all jobs
              </p>
            </div>
          </div>
        </div>

        {/* ── Sales mode ── Money figures only; the note under the buttons says
            so, so nobody reads a switch here as having moved the stock numbers. */}
        <div style={{ ...cardStyle, padding: 20, marginBottom: 20 }}>
          <p style={{ color: '#6B7C93', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
            Sales figures
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {SALES_MODES.map(m => {
              const active = salesMode === m.key;
              return (
                <button key={m.key} onClick={() => setSalesMode(m.key)}
                  style={{
                    ...darkButtonStyle,
                    background: active ? '#0077B6' : '#FFFFFF',
                    color: active ? '#FFFFFF' : '#03045E',
                    border: active ? '1px solid #0077B6' : '1px solid #E3ECF5',
                    fontWeight: active ? 800 : 600,
                  }}>
                  {m.label}
                </button>
              );
            })}
          </div>
          <p style={{ color: '#6B7C93', fontSize: 12, margin: '12px 0 0 0' }}>
            <strong style={{ color: '#03045E' }}>{activeMode.note}.</strong>{' '}
            This affects the revenue, quote and invoice figures and the Sales by Material dollars.
            It does <strong style={{ color: '#03045E' }}>not</strong> affect inventory: material
            leaves the warehouse on an installation job just as it does over the counter, so the
            stock figures below always cover every job.
          </p>
        </div>

        {/* Filters */}
        <div style={{ ...cardStyle, padding: 20, marginBottom: 20 }}>
          <p style={{ color: '#6B7C93', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700, marginBottom: 12 }}>
            Filters
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            <div>
              <label style={labelStyle}>From Date</label>
              <input type="date" value={dateRangeStart || ''} onChange={(e) => setDateRangeStart(e.target.value || null)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>To Date</label>
              <input type="date" value={dateRangeEnd || ''} onChange={(e) => setDateRangeEnd(e.target.value || null)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Salesperson</label>
              <select value={salespersonFilter} onChange={(e) => setSalespersonFilter(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="All" style={{ background: '#ffffff' }}>All Salespersons</option>
                {salespersons.map(s => <option key={s} value={s} style={{ background: '#ffffff' }}>{s}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button onClick={() => { setDateRangeStart(null); setDateRangeEnd(null); setSalespersonFilter('All'); }} style={darkButtonStyle}>
                ↺ Reset Filters
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ ...cardStyle, padding: '60px 20px', textAlign: 'center', color: '#6B7C93' }}>
            <p style={{ fontSize: 15, margin: 0 }}>Loading reports...</p>
          </div>
        ) : (
          <>
            {/* ── SALES (money) — the section the mode applies to ── */}
            <p style={{ color: '#03045E', fontSize: 13, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 10px 0' }}>
              Sales — {activeMode.label.toLowerCase()} · {activeMode.note.toLowerCase()}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 8 }}>
              <StatCard label="Revenue This Month" value={`$${revenueThisMonth.toFixed(2)}`} accent="cyan" />
              <StatCard label="Revenue Year to Date" value={`$${revenueYTD.toFixed(2)}`} accent="violet" />
              {/* Quotes are not on the board, so in Installation jobs mode this
                  figure DOES NOT APPLY — it is not a count of zero. A muted dash
                  says that; a 0 would read as "the install side quoted nothing". */}
              <StatCard label="Quotes This Year"
                value={salesMode === 'install' ? '—' : quotesYTD}
                muted={salesMode === 'install'}
                accent="green" />
              <StatCard label="Invoices This Year" value={invoicesYTD} accent="orange" />
            </div>
            {/* One line, directly under the tiles, in the mode it applies to.
                Both branches are a single line at the same font size, so
                switching modes does not move anything below them. */}
            {salesMode === 'install' && (
              <p style={{ color: '#6B7C93', fontSize: 12, margin: '0 0 20px 0' }}>
                Quotes This Year does not apply: quotes are not assigned to a board column, so they cannot be split by mode.
              </p>
            )}
            {salesMode === 'counter' && (
              <p style={{ color: '#6B7C93', fontSize: 12, margin: '0 0 20px 0' }}>
                Quotes are not assigned to a board column, so every quote counts as a counter sale.
              </p>
            )}
            {salesMode === 'all' && <div style={{ marginBottom: 12 }} />}

            {/* Sales by material */}
            <div style={{ ...cardStyle, padding: 24, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <h3 style={{ color: '#03045E', fontSize: 18, fontWeight: 800, margin: 0 }}>📊 Sales by Material</h3>
                  <p style={{ color: '#6B7C93', fontSize: 12, margin: '4px 0 0 0' }}>
                    Last 30 days · Invoices only · {activeMode.note} — these are revenue dollars, so
                    they follow the sales mode
                  </p>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 16 }}>
                {salesByMaterial.map(tile => (
                  <div key={tile.key} style={{
                    background: '#CDE7F5', border: '1px solid #E3ECF5', borderRadius: 12,
                    padding: '22px 16px', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 34, lineHeight: 1, marginBottom: 10 }}>{tile.emoji}</div>
                    <div style={{ color: '#03045E', fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{tile.key}</div>
                    <div style={{ color: tile.color, fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
                      {Math.round(tile.pct)}%
                    </div>
                    <div style={{ color: '#6B7C93', fontSize: 13, fontWeight: 600, marginTop: 8 }}>
                      {fmtMoney(tile.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── INVENTORY — never filtered by the sales mode ── */}
            <p style={{ color: '#03045E', fontSize: 13, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', margin: '0 0 4px 0' }}>
              Inventory — all jobs included
            </p>
            <p style={{ color: '#6B7C93', fontSize: 12, margin: '0 0 12px 0' }}>
              Stock levels count every job, installation and counter alike, in all three modes —
              material leaves the warehouse either way. These figures come from the products
              themselves and do not change when the sales mode above changes.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 20 }}>
              {/* Out of stock */}
              <div style={{ ...cardStyle, border: '1px solid #E9C3C3', padding: 24 }}>
                <h3 style={{ color: '#A32D2D', fontSize: 18, fontWeight: 800, margin: '0 0 8px 0' }}>🔴 Out of Stock</h3>
                <p style={{ color: '#6B7C93', fontSize: 12, margin: '0 0 16px 0' }}>
                  {stockAlerts.outOfStock.length} product{stockAlerts.outOfStock.length !== 1 ? 's' : ''} need{stockAlerts.outOfStock.length === 1 ? 's' : ''} immediate reorder
                </p>
                {stockAlerts.outOfStock.length === 0 ? (
                  <p style={{ color: '#6B7C93', fontSize: 13, textAlign: 'center', padding: 20 }}>✓ All products in stock</p>
                ) : (
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {stockAlerts.outOfStock.map(p => <StockProgressRow key={p.id} product={p} type="out" />)}
                  </div>
                )}
              </div>

              {/* Low stock */}
              <div style={{ ...cardStyle, border: '1px solid #E6C998', padding: 24 }}>
                <h3 style={{ color: '#854F0B', fontSize: 18, fontWeight: 800, margin: '0 0 8px 0' }}>🟡 Low Stock</h3>
                <p style={{ color: '#6B7C93', fontSize: 12, margin: '0 0 16px 0' }}>
                  {stockAlerts.lowStock.length} product{stockAlerts.lowStock.length !== 1 ? 's' : ''} below reorder point
                </p>
                {stockAlerts.lowStock.length === 0 ? (
                  <p style={{ color: '#6B7C93', fontSize: 13, textAlign: 'center', padding: 20 }}>✓ All stock levels healthy</p>
                ) : (
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {stockAlerts.lowStock.map(p => <StockProgressRow key={p.id} product={p} type="low" />)}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      </div>
    </>
  );
}

export default Reports;
